/**
 * Created by katarinababic on 23.3.23.
 */
import * as React from 'react';
import { observer } from 'mobx-react';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import { Container, Col } from 'reactstrap';
import { AuthOverlayLeft } from './AuthOverlayLeft';
import { useTrans } from '../../../Store/System/LocalizationStore';
import { AuthOverlayRight } from './AuthOverlayRight';
import { InputHTMLAttributes, PropsWithChildren } from 'react';
import { CookieConsentBanner } from '../../CookieConsentBanner';
import { AuthLayoutBlackBox, AuthLayoutBlackBoxProps } from './AuthLayoutBlackBox';
import { ServiceLifecycleCard } from '../../ServiceLifecycleBoundary';

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const AuthWrapper = styled(Container)`
  display: flex;
  flex-grow: 1 !important;
  background-size: cover !important;
  background-repeat: no-repeat !important;
  background-position: center 40% !important;
  background:
    linear-gradient(180deg, rgba(0, 0, 0, 0.3) 0%, rgba(0, 0, 0, 0.5) 50%, rgba(0, 0, 0, 0.85) 100%),
    url('${({ image }) => image}');
  padding: 0;
  animation: ${fadeIn} 0.8s ease-out;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: -50%;
    left: -50%;
    width: 200%;
    height: 200%;
    background:
      radial-gradient(circle at 30% 20%, rgba(0, 221, 163, 0.08) 0%, transparent 50%),
      radial-gradient(circle at 70% 80%, rgba(0, 175, 150, 0.08) 0%, transparent 50%);
    pointer-events: none;
  }
`;

const OverlayColumn = styled(Col)`
  display: none;

  @media (min-width: 768px) {
    display: flex;
    flex-direction: column;
    padding: 0 2rem 5rem 2rem;
  }
`;

const BlackBoxColumn = styled(Col)`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  flex-grow: 1 !important;
  position: relative;
  z-index: 1;
  padding: 2rem 0;

  @media (min-width: 768px) {
    padding: 3rem 0;
  }
`;

const MobileCookiesContainer = styled.div`
  position: absolute;
  top: -1rem;
  left: 0;
  right: 0;
  z-index: 2;
  @media (min-width: 768px) {
    display: none;
  }
`;

const DesktopCookiesContainer = styled.div`
  display: none;
  @media (min-width: 768px) {
    display: flex;
    justify-content: center;
    margin-top: 1.5rem;
  }
`;

export type AuthLayoutProps = AuthLayoutBlackBoxProps & {
  image: InputHTMLAttributes<HTMLImageElement>['src'];
  overlayRightTitle: string;
  footer?: React.ReactNode;
};

export const AuthLayout: React.FC<PropsWithChildren<AuthLayoutProps>> = observer(
  ({ title, subtitle, text, image, children, overlayRightTitle, footer }) => {
    const { t } = useTrans();

    return (
      <React.Fragment>
        <ServiceLifecycleCard />
        <AuthWrapper fluid={true} image={image}>
          <OverlayColumn xs={2} md={3} lg={4}>
            <AuthOverlayLeft
              text={t('registration.stepOne.introSubtitle', { defaultValue: 'For all around healthy employees' })}
            />
          </OverlayColumn>
          <BlackBoxColumn xs={12} md={6} lg={4}>
            <MobileCookiesContainer>
              <CookieConsentBanner />
            </MobileCookiesContainer>
            <AuthLayoutBlackBox title={title} subtitle={subtitle} text={text}>
              {children}
            </AuthLayoutBlackBox>
            {footer}
            <DesktopCookiesContainer>
              <CookieConsentBanner />
            </DesktopCookiesContainer>
          </BlackBoxColumn>
          <OverlayColumn xs={2} md={3} lg={4}>
            <AuthOverlayRight text={overlayRightTitle} />
          </OverlayColumn>
        </AuthWrapper>
      </React.Fragment>
    );
  },
);
